import type { WorkOrderPriceItem } from 'gql/graphql';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';

export enum DeletableWorkOrderItemsStatus {
    noDeletable = 'noDeletable',
    deletable = 'deletable',
    partlyDeletable = 'partlyDeletable',
}

interface IDeletableMessage {
    message: string;
    itemsIds: number[];
}

export interface IUseDeletableWorkOrderItemsResponse {
    status: DeletableWorkOrderItemsStatus;
    deletableIds?: number[];
    messages?: Record<WorkOrderPriceTypeEnum, IDeletableMessage>;
}

const mtrMessage = 'Имеется фактическое выполнение. Чтобы удалить строку, очистите значения в ячейках Акта';
const message =
    'Имеется фактическое выполнение. Чтобы удалить строку, очистите значения в ячейках Акта для расценки и дочерних сущностей';

const defaultMessages: Record<WorkOrderPriceTypeEnum, IDeletableMessage> = {
    [WorkOrderPriceTypeEnum.Mtr]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.MtrContractorEquipment]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.MtrContractorMaterial]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.MtrCustomerEquipment]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.MtrCustomerMaterial]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.Uer]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.Coefficient]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.ConstraintCoefficient]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.TechPartCoefficient]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.DismantlingCoefficient]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.Other]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.OtherGroup]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.Object]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.RootObject]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.None]: {
        message,
        itemsIds: [],
    },
};

export const useCheckDeletableWorkOrder = (
    rowData: WorkOrderPriceItem | null | undefined,
    workOrderStatus: WorkOrderStatusEnum | undefined,
): IUseDeletableWorkOrderItemsResponse => {
    if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
        return {
            status: DeletableWorkOrderItemsStatus.deletable,
        };
    }

    if (workOrderStatus && [WorkOrderStatusEnum.Completed, WorkOrderStatusEnum.Delayed].includes(workOrderStatus)) {
        return { status: DeletableWorkOrderItemsStatus.noDeletable };
    }

    const messages = { ...defaultMessages };
    const deletableIds: number[] = [];

    const doCheck = (item: WorkOrderPriceItem) => {
        if (item?.isActKC2Price && item?.type) {
            const isRatioItem = [
                WorkOrderPriceTypeEnum.ConstraintCoefficient,
                WorkOrderPriceTypeEnum.TechPartCoefficient,
                WorkOrderPriceTypeEnum.Coefficient,
                WorkOrderPriceTypeEnum.DismantlingCoefficient,
            ].includes(item.type);

            const hasEmptyFactValue =
                item?.type === WorkOrderPriceTypeEnum.Other
                    ? item?.hasEmptyActPlanCost && item?.hasEmptyActAcceptedCost
                    : item?.hasEmptyActPlanVolume && item?.hasEmptyActAcceptedVolume;

            if (hasEmptyFactValue || isRatioItem) {
                deletableIds.push(item.itemId);
                messages[item.type].itemsIds = messages[item.type].itemsIds.filter(
                    (messagesItem) => messagesItem !== item.itemId,
                );
            } else {
                messages[item.type].itemsIds.push(item.itemId);
            }
        }
    };

    if (rowData) {
        if (Array.isArray(rowData)) {
            rowData.forEach((item) => {
                doCheck(item);
            });
        } else {
            doCheck(rowData);
        }
    }

    return { status: DeletableWorkOrderItemsStatus.partlyDeletable, deletableIds, messages };
};
